import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {useTelegram} from '../../hooks/useTelegram';
import {User, Key, ArrowRight, ArrowLeft, RefreshCw, Zap, Shield, Lock} from 'lucide-react';
import './LoginForm.css';

const LoginForm = () => {
    const {t} = useTranslation();
    const {tg_user} = useTelegram();
    const [step, setStep] = useState(1);
    const [nickname, setNickname] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [userId, setUserId] = useState(null);
    const [timer, setTimer] = useState(120);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [timerInterval, setTimerInterval] = useState(null);
    const navigate = useNavigate();
    const otpInputsRef = useRef([]);

    // OTP Input handlers
    const handleOtpChange = (index, value) => {
        if (value.length > 1) {
            // Handle paste
            const pastedValue = value.slice(0, 6).split('');
            const newOtp = [...otp];
            pastedValue.forEach((char, i) => {
                if (i < 6 && /[0-9]/.test(char)) {
                    newOtp[i] = char;
                }
            });
            setOtp(newOtp);
            const lastFilledIndex = pastedValue.length - 1;
            if (lastFilledIndex < 5) {
                otpInputsRef.current[lastFilledIndex + 1]?.focus();
            }
            return;
        }

        if (!/^[0-9]*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            otpInputsRef.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputsRef.current[index - 1]?.focus();
        }
    };

    const getOtpValue = () => otp.join('');

    // Request OTP
    const requestOtp = async (e) => {
        e.preventDefault();
        if (!nickname.trim()) {
            setError(t('auth.enterUsernameError'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post('/api/login', {nickname});
            setUserId(response.data.user_id);
            setStep(2);
            setOtp(['', '', '', '', '', '']);
            setSuccess(t('auth.otpSent'));

            if (timerInterval) {
                clearInterval(timerInterval);
            }

            let timeLeft = response.data.expires_in || 120;
            setTimer(timeLeft);

            const countdownInterval = setInterval(() => {
                timeLeft -= 1;
                setTimer(timeLeft);

                if (timeLeft <= 0) {
                    clearInterval(countdownInterval);
                    setStep(1);
                    setError(t('auth.otpExpired'));
                }
            }, 1000);

            setTimerInterval(countdownInterval);

            // Focus first OTP input
            setTimeout(() => otpInputsRef.current[0]?.focus(), 100);

        } catch (err) {
            setError(err.response?.data?.detail || t('auth.otpRequestError'));
        } finally {
            setLoading(false);
        }
    };

    // Verify OTP
    const verifyOtp = async (e) => {
        e.preventDefault();
        const otpValue = getOtpValue();
        if (otpValue.length !== 6) {
            setError(t('auth.enterOtpError'));
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post('/api/verify-otp', {user_id: userId, otp: otpValue});

            if (timerInterval) {
                clearInterval(timerInterval);
            }

            setSuccess(t('auth.loginSuccess'));

            localStorage.setItem('token', response.data.access_token);
            const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
            localStorage.setItem('tokenExpiry', new Date().getTime() + thirtyDaysInMs);
            localStorage.setItem('user_id', userId);

            setTimeout(() => {
                navigate('/connections');
            }, 1000);

        } catch (err) {
            setError(err.response?.data?.detail || t('auth.invalidOtp'));
            setOtp(['', '', '', '', '', '']);
            otpInputsRef.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP
    const resendOtp = async () => {
        if (loading) return;

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await axios.post('/api/login', {nickname});

            if (timerInterval) {
                clearInterval(timerInterval);
            }

            let timeLeft = response.data.expires_in || 120;
            setTimer(timeLeft);

            const countdownInterval = setInterval(() => {
                timeLeft -= 1;
                setTimer(timeLeft);

                if (timeLeft <= 0) {
                    clearInterval(countdownInterval);
                    setStep(1);
                    setError(t('auth.otpExpired'));
                }
            }, 1000);

            setTimerInterval(countdownInterval);
            setOtp(['', '', '', '', '', '']);
            setSuccess(t('auth.otpResent'));
            otpInputsRef.current[0]?.focus();

        } catch (err) {
            setError(err.response?.data?.detail || t('auth.otpRequestError'));
        } finally {
            setLoading(false);
        }
    };

    // Auto-fill nickname from Telegram
    useEffect(() => {
        if (tg_user?.username) {
            setNickname(tg_user.username);
        } else if (tg_user?.first_name) {
            setNickname(tg_user.first_name);
        }
    }, [tg_user]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [timerInterval]);

    // Auto-submit OTP when all digits entered
    useEffect(() => {
        if (step === 2 && otp.every(d => d !== '') && !loading) {
            verifyOtp({preventDefault: () => {}});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp, step]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const timerPercentage = (timer / 120) * 100;

    return (
        <div className="login-page">
            {/* Background Effects */}
            <div className="login-bg">
                <div className="login-bg-gradient"></div>
                <div className="login-bg-pattern"></div>
                <div className="login-bg-glow login-bg-glow-1"></div>
                <div className="login-bg-glow login-bg-glow-2"></div>
            </div>

            <div className="login-container">
                {/* Brand Header */}
                <div className="login-brand">
                    <div className="login-brand-icon">
                        <Zap size={32} />
                    </div>
                    <h1 className="login-brand-name">Hedger</h1>
                    <p className="login-brand-tagline">Professional Trading Panel</p>
                </div>

                {/* Login Card */}
                <div className="login-card">
                    {/* Step Indicator */}
                    <div className="login-steps">
                        <div className={`login-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                            <div className="login-step-icon">
                                <User size={18} />
                            </div>
                            <span className="login-step-label">Identity</span>
                        </div>
                        <div className="login-step-connector">
                            <div className={`login-step-connector-fill ${step > 1 ? 'active' : ''}`}></div>
                        </div>
                        <div className={`login-step ${step >= 2 ? 'active' : ''}`}>
                            <div className="login-step-icon">
                                <Key size={18} />
                            </div>
                            <span className="login-step-label">Verify</span>
                        </div>
                    </div>

                    {/* Messages */}
                    {success && (
                        <div className="login-message login-message-success">
                            <Shield size={20} />
                            <span>{success}</span>
                        </div>
                    )}
                    {error && (
                        <div className="login-message login-message-error">
                            <span className="login-message-icon">!</span>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Step 1: Username */}
                    {step === 1 && (
                        <form onSubmit={requestOtp} className="login-form-content">
                            <div className="login-form-header">
                                <h2>Welcome Back</h2>
                                <p>Enter your username to receive a verification code</p>
                            </div>

                            <div className="login-input-group">
                                <label htmlFor="nickname">
                                    <User size={16} />
                                    <span>{t('auth.username')}</span>
                                </label>
                                <div className="login-input-wrapper">
                                    <input
                                        type="text"
                                        id="nickname"
                                        value={nickname}
                                        onChange={(e) => setNickname(e.target.value)}
                                        placeholder={t('auth.enterUsername')}
                                        disabled={loading}
                                        autoFocus
                                        autoComplete="username"
                                    />
                                </div>
                            </div>

                            <button type="submit" className="login-submit-btn" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="login-spinner"></span>
                                        <span>{t('common.loading')}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{t('auth.getOtp')}</span>
                                        <ArrowRight size={20} />
                                    </>
                                )}
                            </button>

                            <div className="login-security-note">
                                <Lock size={14} />
                                <span>Secure authentication via Telegram</span>
                            </div>
                        </form>
                    )}

                    {/* Step 2: OTP Verification */}
                    {step === 2 && (
                        <form onSubmit={verifyOtp} className="login-form-content">
                            <div className="login-form-header">
                                <h2>Verify Code</h2>
                                <p>Enter the 6-digit code sent to your Telegram</p>
                            </div>

                            {/* OTP Timer */}
                            <div className="otp-timer-wrapper">
                                <div className="otp-timer-bar">
                                    <div
                                        className="otp-timer-progress"
                                        style={{width: `${timerPercentage}%`}}
                                    ></div>
                                </div>
                                <div className="otp-timer-text">
                                    <span>Code expires in</span>
                                    <span className="otp-timer-value">{formatTime(timer)}</span>
                                </div>
                            </div>

                            {/* OTP Input */}
                            <div className="otp-input-group">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={el => otpInputsRef.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onFocus={(e) => e.target.select()}
                                        className={`otp-input ${digit ? 'filled' : ''}`}
                                        disabled={loading}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            <button type="submit" className="login-submit-btn" disabled={loading || getOtpValue().length !== 6}>
                                {loading ? (
                                    <>
                                        <span className="login-spinner"></span>
                                        <span>{t('common.loading')}</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{t('auth.login')}</span>
                                        <ArrowRight size={20} />
                                    </>
                                )}
                            </button>

                            <div className="login-actions">
                                <button
                                    type="button"
                                    className="login-action-btn"
                                    onClick={() => {
                                        if (timerInterval) {
                                            clearInterval(timerInterval);
                                        }
                                        setStep(1);
                                        setOtp(['', '', '', '', '', '']);
                                    }}
                                    disabled={loading}
                                >
                                    <ArrowLeft size={18} />
                                    <span>{t('common.back')}</span>
                                </button>

                                <button
                                    type="button"
                                    className="login-action-btn"
                                    onClick={resendOtp}
                                    disabled={loading || timer > 100}
                                >
                                    <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                                    <span>{t('auth.resendOtp')}</span>
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="login-footer">
                    <p>Protected by end-to-end encryption</p>
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
